import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Add, DeleteOutline, EditOutlined, Save } from '@mui/icons-material';
import SettingsContentCard from './SettingsContentCard';
import { getTerminals } from '../../services/LocationService';
import { deletePaymentProviderTerminalConfiguration, getPaymentProviderConfiguration, getPaymentProviderTerminals, savePaymentProviderAccount, savePaymentProviderCredentials, savePaymentProviderTerminal } from '../../services/PaymentProviderService';
import SnackbarUtils from '../../utils/SnackbarUtils';

// Providers Axium plans to support; the ones the backend can sell with arrive in `configuration.providers`, which is what
// the forms below are built from (labels, whether a merchant code applies, which secrets it needs).
const KNOWN_PROVIDERS = [
  { code: 'GETNET', label: 'Getnet' },
  { code: 'HANDY', label: 'Handy' },
  { code: 'MERCADO_PAGO', label: 'Mercado Pago' },
  { code: 'FISERV', label: 'Fiserv' },
  { code: 'SCANNTECH', label: 'Scanntech' },
];
const blankTerminal = { remoteTerminalId: '', active: false };
const emptyConfiguration = { accounts: [], terminals: [], providers: [], locationRut: null };
const noDiscovery = { status: 'idle', terminals: [], error: '' };

// Credentials belong to the RUT (the legal entity the provider knows), not to each terminal: providers that ask for a merchant
// code or a required secret get one account per RUT, loaded once and shared by every terminal of that RUT in any branch.
const needsRutCredentials = (descriptor) => Boolean(descriptor?.merchantIdLabel)
  || (descriptor?.credentialFields || []).some((field) => !field.optional);

export default function PosIntegrationSettingsTab({ locationId, locationName, readOnly = false }) {
  const [configuration, setConfiguration] = useState(emptyConfiguration);
  const [terminals, setTerminals] = useState([]);
  const [provider, setProvider] = useState('');
  const [terminalId, setTerminalId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [terminalConfig, setTerminalConfig] = useState(blankTerminal);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingTerminalId, setTogglingTerminalId] = useState(null);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [deletingMappingId, setDeletingMappingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Credentials of the RUT: which provider's form is open, and what was typed (secrets are write-only, never read back).
  const [credentialsProvider, setCredentialsProvider] = useState(null);
  const [merchantIdValue, setMerchantIdValue] = useState('');
  const [credentialValues, setCredentialValues] = useState({});
  const [savingCredentials, setSavingCredentials] = useState(false);
  // Terminals the provider's account has, offered to pick from (typing the id by hand always works).
  const [discovery, setDiscovery] = useState(noDiscovery);

  const locationRut = configuration.locationRut || '';
  const providers = configuration.providers || [];
  const accounts = configuration.accounts || [];
  const mappings = configuration.terminals || [];
  const descriptorOf = (code) => providers.find((item) => item.code === code);
  const labelOf = (code) => descriptorOf(code)?.displayName || KNOWN_PROVIDERS.find((item) => item.code === code)?.label || code;
  const descriptor = descriptorOf(provider);
  const providerLabel = labelOf(provider);
  const selectedTerminal = terminals.find((item) => String(item.id) === String(terminalId));
  const accountOf = (id) => accounts.find((item) => String(item.id) === String(id));
  // The account that charges for this branch's RUT.
  const rutAccountOf = (code) => (locationRut ? accounts.find((item) => item.provider === code && item.rut === locationRut) : undefined);
  const providerMappings = mappings.filter((item) => item.provider === provider);
  const savedMapping = mappings.find((item) => item.provider === provider && String(item.terminalId) === String(terminalId));
  // Editing keeps the account the terminal already sells with; a new terminal uses the branch's RUT account.
  const terminalAccount = accountOf(savedMapping?.providerAccountId) || rutAccountOf(provider);
  const accountReady = (account, code) => {
    const target = descriptorOf(code);
    if (!needsRutCredentials(target)) return true;
    return Boolean(account && account.hasCredentials && (!target.merchantIdLabel || account.merchantId));
  };
  const missingCredentials = Boolean(descriptor) && needsRutCredentials(descriptor) && !accountReady(terminalAccount, provider);

  useEffect(() => {
    const load = async () => {
      if (!locationId) return;
      setLoading(true);
      try {
        const [saved, locationTerminals] = await Promise.all([getPaymentProviderConfiguration(locationId), getTerminals(locationId)]);
        setConfiguration({ ...emptyConfiguration, ...(saved || {}) });
        setProvider((current) => current || saved?.providers?.[0]?.code || '');
        setTerminals(locationTerminals || []);
        setTerminalId(String(locationTerminals?.[0]?.id || ''));
      } catch (error) {
        console.error('No se pudo cargar la configuración POS', error);
        SnackbarUtils.error('No se pudo cargar la configuración del POS');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [locationId]);

  useEffect(() => {
    setAccountId(terminalAccount?.id ? String(terminalAccount.id) : '');
    setTerminalConfig(savedMapping ? { remoteTerminalId: savedMapping.remoteTerminalId || '', active: savedMapping.active !== false } : blankTerminal);
  }, [provider, terminalId, configuration.accounts, configuration.terminals, configuration.locationRut]);

  const searchTerminals = async () => {
    if (!descriptor?.supportsTerminalDiscovery) return;
    setDiscovery({ ...noDiscovery, status: 'loading' });
    try {
      const found = await getPaymentProviderTerminals(locationId, provider, accountId ? Number(accountId) : undefined);
      setDiscovery({ status: 'ok', terminals: found, error: '' });
    } catch (error) {
      const data = error?.response?.data;
      setDiscovery({ status: 'error', terminals: [], error: data?.error_message || data?.message || error?.message || 'error desconocido' });
    }
  };

  // When the form opens (or the provider/account changes) the account's terminals are looked up on their own.
  useEffect(() => {
    if (dialogOpen && descriptor?.supportsTerminalDiscovery) searchTerminals();
    else setDiscovery(noDiscovery);
  }, [dialogOpen, provider, accountId, descriptor?.supportsTerminalDiscovery]);

  const assignedTerminalIds = new Set(mappings.filter((item) => item.provider === provider && String(item.terminalId) !== String(terminalId)).map((item) => item.remoteTerminalId));
  const pickedTerminal = discovery.terminals.find((item) => item.id === terminalConfig.remoteTerminalId.trim());

  const openCredentials = (code) => {
    const account = rutAccountOf(code);
    setCredentialsProvider(code);
    setMerchantIdValue(account?.merchantId || '');
    setCredentialValues({});
  };

  const credentialsDescriptor = descriptorOf(credentialsProvider);
  const credentialsAccount = rutAccountOf(credentialsProvider);
  const credentialFields = credentialsDescriptor?.credentialFields || [];
  const typedSecrets = Object.fromEntries(credentialFields.map((field) => [field.key, (credentialValues[field.key] || '').trim()]).filter(([, value]) => value));

  const saveCredentials = async () => {
    const label = labelOf(credentialsProvider);
    if (!locationRut) return SnackbarUtils.warning('La sucursal no tiene RUT configurado. Cargalo en la facturación de la sucursal.');
    if (credentialsDescriptor.merchantIdLabel && !merchantIdValue.trim()) return SnackbarUtils.warning(`Ingresá el ${credentialsDescriptor.merchantIdLabel} de ${label}.`);
    const missingSecret = credentialFields.find((field) => !field.optional && !typedSecrets[field.key] && !credentialsAccount?.hasCredentials);
    if (missingSecret) return SnackbarUtils.warning(`Ingresá ${missingSecret.label} entregado por ${label}.`);
    setSavingCredentials(true);
    try {
      const savedAccount = await savePaymentProviderAccount(locationId, {
        provider: credentialsProvider,
        display_name: `${label} ${locationRut}`,
        merchant_id: credentialsDescriptor.merchantIdLabel ? merchantIdValue.trim() : null,
        rut: locationRut,
        active: true,
      });
      let hasCredentials = savedAccount.hasCredentials;
      if (Object.keys(typedSecrets).length) {
        await savePaymentProviderCredentials(locationId, savedAccount.id, typedSecrets);
        hasCredentials = true;
      }
      setConfiguration((current) => ({
        ...current,
        accounts: [...(current.accounts || []).filter((item) => item.id !== savedAccount.id), { ...savedAccount, hasCredentials }],
      }));
      setCredentialsProvider(null);
      SnackbarUtils.success(`Credenciales de ${label} guardadas para el RUT ${locationRut}.`);
    } catch (error) {
      console.error(`No se pudieron guardar las credenciales de ${label}`, error);
      SnackbarUtils.error('No se pudieron guardar las credenciales');
    } finally {
      setSavingCredentials(false);
    }
  };

  const selectMapping = (mapping) => {
    setProvider(mapping.provider);
    setTerminalId(String(mapping.terminalId));
    setDialogOpen(true);
  };

  const openNewConfiguration = () => {
    const free = terminals.find((item) => !providerMappings.some((mapping) => String(mapping.terminalId) === String(item.id)));
    setTerminalId(String((free || terminals[0])?.id || ''));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!terminalId) return SnackbarUtils.warning('Seleccioná una terminal Axium.');
    if (!descriptor) return SnackbarUtils.warning('Seleccioná un proveedor.');
    if (missingCredentials) return SnackbarUtils.warning(`Cargá primero las credenciales de ${providerLabel} para el RUT.`);
    if (!terminalConfig.remoteTerminalId.trim()) return SnackbarUtils.warning(`Ingresá el ${descriptor.remoteTerminalIdLabel} de ${providerLabel}.`);
    setSaving(true);
    try {
      let account = terminalAccount;
      // Providers without credentials of their own (Mercado Pago uses the company's integration) get their account on first use.
      if (!account) {
        if (!locationRut) return SnackbarUtils.warning('La sucursal no tiene RUT configurado. Cargalo en la facturación de la sucursal.');
        account = await savePaymentProviderAccount(locationId, { provider, display_name: `${providerLabel} ${locationRut}`, rut: locationRut, active: true });
      }
      const savedTerminal = await savePaymentProviderTerminal(locationId, Number(terminalId), {
        provider_account_id: account.id,
        remote_terminal_id: terminalConfig.remoteTerminalId.trim(),
        connection_url: null,
        active: terminalConfig.active,
      });
      setConfiguration((current) => ({
        ...current,
        accounts: [...(current.accounts || []).filter((item) => item.id !== account.id), account],
        terminals: [...(current.terminals || []).filter((item) => !(item.provider === provider && String(item.terminalId) === String(terminalId))), savedTerminal],
      }));
      SnackbarUtils.success(`${providerLabel} quedó asociado a ${selectedTerminal?.reference || selectedTerminal?.code || 'la terminal'}.`);
      setDialogOpen(false);
    } catch (error) {
      console.error('No se pudo guardar la configuración POS', error);
      SnackbarUtils.error('No se pudo guardar la configuración del POS');
    } finally {
      setSaving(false);
    }
  };

  const toggleTerminal = async (mapping, active) => {
    setTogglingTerminalId(mapping.id);
    try {
      const saved = await savePaymentProviderTerminal(locationId, mapping.terminalId, {
        provider_account_id: mapping.providerAccountId,
        merchant_id: null,
        remote_terminal_id: mapping.remoteTerminalId,
        connection_url: null,
        active,
      });
      setConfiguration((current) => ({
        ...current,
        terminals: current.terminals.map((item) => item.id === mapping.id ? saved : item),
      }));
      SnackbarUtils.success(active ? 'Terminal POS activada' : 'Terminal POS desactivada');
    } catch (error) {
      console.error('No se pudo actualizar el estado de la terminal POS', error);
      SnackbarUtils.error('No se pudo actualizar el estado de la terminal POS');
    } finally {
      setTogglingTerminalId(null);
    }
  };

  const deleteConfiguration = async () => {
    if (!mappingToDelete) return;
    setDeletingMappingId(mappingToDelete.id);
    try {
      await deletePaymentProviderTerminalConfiguration(locationId, mappingToDelete.id);
      setConfiguration((current) => ({
        ...current,
        terminals: current.terminals.filter((item) => item.id !== mappingToDelete.id),
      }));
      SnackbarUtils.success('Configuración POS eliminada');
      setMappingToDelete(null);
    } catch (error) {
      console.error('No se pudo eliminar la configuración POS', error);
      SnackbarUtils.error('No se pudo eliminar la configuración POS');
    } finally {
      setDeletingMappingId(null);
    }
  };

  return <Stack spacing={3}>
    {!loading && !locationRut && <Alert severity="warning">Esta sucursal no tiene RUT configurado. Las credenciales del POS son del RUT: cargalo en la facturación de la sucursal para poder configurarlas.</Alert>}

    <SettingsContentCard title="POS" description={`Terminales de cobro de ${locationName || 'la sucursal seleccionada'}, por proveedor.`}>
      {!providers.length ? <Alert severity="info">No hay proveedores de POS disponibles.</Alert> : <>
        <Tabs value={provider || false} onChange={(event, value) => setProvider(value)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          {providers.map((item) => <Tab key={item.code} value={item.code} label={labelOf(item.code)} />)}
        </Tabs>

        {descriptor && needsRutCredentials(descriptor) && (() => {
          const account = rutAccountOf(provider);
          const ready = accountReady(account, provider);
          return <Card variant="outlined" sx={{ mb: 3 }}><CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box>
                <Typography fontWeight={700}>Credenciales del RUT {locationRut}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {ready ? `${descriptor.merchantIdLabel ? `${descriptor.merchantIdLabel}: ${account.merchantId} · ` : ''}se cargan una vez y valen para todas las terminales de este RUT, en cualquier sucursal.` : `Todavía no hay credenciales de ${providerLabel} para este RUT. Se cargan una vez y valen para todas sus terminales.`}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Chip size="small" color={ready ? 'success' : 'default'} label={ready ? 'Configuradas ✓' : 'Pendientes'} />
                <Button size="small" variant={ready ? 'text' : 'contained'} startIcon={<EditOutlined />} onClick={() => openCredentials(provider)} disabled={readOnly || loading || !locationRut}>{ready ? 'Editar' : 'Cargar credenciales'}</Button>
              </Box>
            </Box>
          </CardContent></Card>;
        })()}
        {descriptor && !needsRutCredentials(descriptor) && <Alert severity="info" sx={{ mb: 3 }}>{`${providerLabel} cobra con la cuenta configurada en Configuración › ${providerLabel} para esta sucursal (o su franquicia): acá no se cargan credenciales, solo se asocian las terminales.`}</Alert>}

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 2 }} flexWrap="wrap">
          <Typography fontWeight={700}>Terminales de {providerLabel}</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openNewConfiguration} disabled={readOnly || loading || !provider}>Agregar configuración</Button>
        </Box>
        {!providerMappings.length ? <Alert severity="info">Todavía no hay terminales de {providerLabel} configuradas en esta sucursal.</Alert> : <Grid container spacing={2}>
          {providerMappings.map((mapping) => {
            const mappedTerminal = terminals.find((item) => String(item.id) === String(mapping.terminalId));
            const mappingAccount = accountOf(mapping.providerAccountId);
            return <Grid item xs={12} md={6} key={`${mapping.provider}-${mapping.terminalId}`}><Card variant="outlined" sx={{ height: '100%' }}><CardContent><Stack spacing={1}>
              <Box display="flex" justifyContent="space-between" gap={1} alignItems="flex-start"><Box><Typography fontWeight={700}>{mappedTerminal?.reference || mappedTerminal?.code || `Terminal #${mapping.terminalId}`}</Typography>{mappingAccount?.rut && <Typography variant="body2" color="text.secondary">RUT {mappingAccount.rut}</Typography>}</Box><Box display="flex" alignItems="center" gap={0.5}><Typography variant="caption" color={mapping.active ? 'success.main' : 'text.secondary'}>{mapping.active ? 'Activa' : 'Inactiva'}</Typography><Switch checked={mapping.active} onChange={(event) => toggleTerminal(mapping, event.target.checked)} disabled={readOnly || togglingTerminalId === mapping.id} inputProps={{ 'aria-label': `Activar ${mappedTerminal?.reference || mappedTerminal?.code || 'terminal'} ` }} /></Box></Box>
              <Typography variant="body2">{descriptor?.remoteTerminalIdLabel || 'Terminal proveedor'}: <strong>{mapping.remoteTerminalId}</strong></Typography>
              <Box display="flex" gap={1}><Button size="small" startIcon={<EditOutlined />} onClick={() => selectMapping(mapping)}>Editar</Button><Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => setMappingToDelete(mapping)} disabled={readOnly || deletingMappingId === mapping.id}>Eliminar</Button></Box>
            </Stack></CardContent></Card></Grid>;
          })}
        </Grid>}
      </>}
    </SettingsContentCard>

    <Dialog open={Boolean(mappingToDelete)} onClose={() => !deletingMappingId && setMappingToDelete(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Eliminar configuración POS</DialogTitle>
      <DialogContent><Typography>Vas a eliminar la configuración de {labelOf(mappingToDelete?.provider)} para esta terminal. El historial de cobros se conserva.</Typography></DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}><Button onClick={() => setMappingToDelete(null)} disabled={Boolean(deletingMappingId)}>Cancelar</Button><Button color="error" variant="contained" startIcon={<DeleteOutline />} onClick={deleteConfiguration} disabled={Boolean(deletingMappingId)}>{deletingMappingId ? 'Eliminando…' : 'Eliminar configuración'}</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(credentialsProvider)} onClose={() => !savingCredentials && setCredentialsProvider(null)} fullWidth maxWidth="sm">
      <DialogTitle>Credenciales de {labelOf(credentialsProvider)} · RUT {locationRut}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">Son las que {labelOf(credentialsProvider)} entrega a este RUT. Se cargan una sola vez: después cada terminal solo necesita su código.</Typography>
          {credentialsDescriptor?.merchantIdLabel && <TextField label={credentialsDescriptor.merchantIdLabel} value={merchantIdValue} onChange={(event) => setMerchantIdValue(event.target.value)} helperText={credentialsDescriptor.merchantIdHelp} fullWidth />}
          {credentialFields.map((field) => <Box key={field.key}>
            {field.help && <Typography variant="body2" color="text.secondary" gutterBottom>{field.help}</Typography>}
            <TextField
              label={credentialsAccount?.hasCredentials ? `Sobrescribir ${field.label}` : field.label}
              type="password"
              value={credentialValues[field.key] || ''}
              onChange={(event) => setCredentialValues((current) => ({ ...current, [field.key]: event.target.value }))}
              helperText={credentialsAccount?.hasCredentials ? 'Configurada ✓. Dejala vacía para no cambiarla.' : ' '}
              fullWidth
            />
          </Box>)}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}><Button onClick={() => setCredentialsProvider(null)} disabled={savingCredentials}>Cancelar</Button><Button variant="contained" startIcon={<Save />} onClick={saveCredentials} disabled={savingCredentials}>{savingCredentials ? 'Guardando…' : 'Guardar credenciales'}</Button></DialogActions>
    </Dialog>

    <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>Configurar terminal {providerLabel}</DialogTitle>
      <DialogContent dividers>
      <Box component="fieldset" disabled={readOnly || loading} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}><FormControl fullWidth><InputLabel>Terminal Axium</InputLabel><Select label="Terminal Axium" value={terminalId} onChange={(event) => setTerminalId(event.target.value)}>{terminals.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.reference || item.code} · {item.code}</MenuItem>)}</Select></FormControl></Grid>
          {descriptor && missingCredentials && <Grid item xs={12}>
            <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => openCredentials(provider)} disabled={!locationRut}>Cargar credenciales</Button>}>
              {locationRut ? `El RUT ${locationRut} todavía no tiene las credenciales de ${providerLabel}. Se cargan una vez y sirven para todas sus terminales.` : 'La sucursal no tiene RUT configurado.'}
            </Alert>
          </Grid>}
          {descriptor && !missingCredentials && <Grid item xs={12}>
            {descriptor.supportsTerminalDiscovery ? <Stack spacing={1}>
              <Autocomplete
                freeSolo
                options={discovery.terminals}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.id)}
                inputValue={terminalConfig.remoteTerminalId}
                onInputChange={(event, value) => setTerminalConfig((current) => ({ ...current, remoteTerminalId: value }))}
                renderOption={(props, option) => <li {...props} key={option.id}>
                  <Box><Typography variant="body2">{option.id}</Typography>
                    <Typography variant="caption" color="text.secondary">{[option.operatingMode && `Modo ${option.operatingMode}`, !option.ready && 'no recibe cobros todavía', assignedTerminalIds.has(option.id) && 'ya asignada a otra terminal'].filter(Boolean).join(' · ') || 'Lista para cobrar'}</Typography></Box>
                </li>}
                renderInput={(params) => <TextField {...params} label={descriptor.remoteTerminalIdLabel} helperText={descriptor.remoteTerminalIdHelp} />}
              />
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Button size="small" variant="outlined" onClick={searchTerminals} disabled={discovery.status === 'loading'}>Buscar terminales</Button>
                {discovery.status === 'loading' && <CircularProgress size={16} />}
                {discovery.status === 'ok' && <Typography variant="caption" color="text.secondary">{discovery.terminals.length ? `${discovery.terminals.length} terminal${discovery.terminals.length === 1 ? '' : 'es'} encontrada${discovery.terminals.length === 1 ? '' : 's'}: elegí una o escribí el ID a mano.` : 'La cuenta no tiene terminales vinculadas: podés escribir el ID a mano.'}</Typography>}
              </Box>
              {discovery.status === 'error' && <Alert severity="warning">No se pudo listar las terminales: {discovery.error}. Podés escribir el ID a mano.</Alert>}
              {pickedTerminal && !pickedTerminal.ready && <Alert severity="warning">Esta terminal está en modo {pickedTerminal.operatingMode}: para cobrar desde Axium ponela en modo Punto de Venta (PDV) y reiniciala.</Alert>}
            </Stack> : <TextField label={descriptor.remoteTerminalIdLabel} value={terminalConfig.remoteTerminalId} onChange={(event) => setTerminalConfig((current) => ({ ...current, remoteTerminalId: event.target.value }))} helperText={descriptor.remoteTerminalIdHelp} fullWidth />}
          </Grid>}
          {descriptor && !missingCredentials && terminalAccount?.merchantId && descriptor.merchantIdLabel && <Grid item xs={12}><Typography variant="body2" color="text.secondary">Se usa el {descriptor.merchantIdLabel} <strong>{terminalAccount.merchantId}</strong> del RUT {terminalAccount.rut || locationRut}.</Typography></Grid>}
        </Grid>
      </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}><Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button><Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving || !terminalId || missingCredentials}>{saving ? 'Guardando…' : 'Guardar configuración'}</Button></DialogActions>
    </Dialog>
  </Stack>;
}
